/**
 * Campaign Mission — Milestone 37.2.
 *
 * Thin frontend hook around ``POST /workspaces/{ws}/campaign-missions/
 * {blueprint_id}/sync``. The mission is authoritative for lifecycle
 * *tracking*; every Campaign business action is unchanged.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { http } from "@/shared/api/http";
import type { Mission } from "./types";
import { missionKeys } from "./hooks";
import { writeLocalMission } from "./memory";

async function syncCampaignMission(
  workspaceId: string,
  blueprintId: string,
): Promise<{ mission: Mission }> {
  return http<{ mission: Mission }>(
    `/workspaces/${workspaceId}/campaign-missions/${blueprintId}/sync`,
    { method: "POST", body: {} },
  );
}

/**
 * Trigger a Campaign mission sync at every existing lifecycle
 * transition point. The mutation is fire-and-forget: on success the
 * resumable list + mission cache are invalidated so Executive Home
 * reflects the new state on the next mount.
 */
export function useSyncCampaignMission(
  workspaceId: string | null,
  userId: string | null,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (blueprintId: string) => {
      if (!workspaceId) throw new Error("workspace required");
      const res = await syncCampaignMission(workspaceId, blueprintId);
      if (userId) writeLocalMission(userId, res.mission);
      return res.mission;
    },
    onSuccess: (mission) => {
      qc.setQueryData(missionKeys.detail(workspaceId, mission.id), mission);
      void qc.invalidateQueries({ queryKey: missionKeys.list(workspaceId) });
    },
  });
}
